import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Page, Banner, Tabs, Card, TextField, Select, EmptyState } from "@shopify/polaris";
import {
  InvoicePermissionHolders,
  PurchaseDocCounts,
  PurchaseDocFilters,
  PurchaseDocListItem,
  PurchaseDocTab,
} from "types/InvoiceInbox";
import {
  fetchInvoicePermissionHolders,
  fetchPurchaseDocCounts,
  refreshInvoiceMailboxes,
  uploadPurchaseDoc,
} from "services/invoice-inbox.service";
import InvoiceDocPanel from "./invoice-doc-panel";
import InvoiceInboxPeriodTree from "./invoice-inbox-period-tree";
import InvoiceDocPreview from "./invoice-doc-preview";
import InvoiceInboxBulkBar from "./invoice-inbox-bulk-bar";
import { BulkAction, BulkActionId, BulkFailure, BulkInput, BulkResult, runBulkAction } from "./invoice-inbox-bulk";
import InvoiceInboxExpectations from "./invoice-inbox-expectations";
import InvoiceInboxMailWithoutDocuments from "./invoice-inbox-mail-without-documents";
import InvoiceInboxActors from "./invoice-inbox-actors";
import { TABS } from "./invoice-inbox-tabs";
import { useAppDispatch, useAppSelector } from "hooks";
import { fetchCabinetSummaryThunk } from "features/cabinet-tasks/cabinet-summary.slice";
import { PHONE_PAGE_GUTTER } from "components/tw/pageGutter";

/**
 * The register.
 *
 * Tabs are QUERY SHAPES, not statuses: "bookkept" is approved-and-entered,
 * and a flag composes with any tab. The tab lives in the URL so a view can be
 * sent to a colleague, and so does the open document (?doc=) — the panel is a
 * view of the list, not a separate page.
 *
 * The month tree (`InvoiceInboxPeriodTree`) is the only way to browse it — an
 * older link with `?view=list` (or `?view=` anything) still opens fine, it
 * just shows the tree like every other link now does.
 */

export default function InvoiceInbox() {
  const { companyId } = useParams<{ companyId: string }>();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  // The settings screen is a narrower right than the register itself: it holds
  // the mailbox credentials, so most approvers never see the link.
  const { role } = useAppSelector((state) => state.authReducer);
  const canManageSettings = Boolean(
    role?.permissionsgroups?.includes("Master") ||
      role?.permissionsgroups?.includes("Manage invoice inbox settings")
  );
  const isMaster = Boolean(role?.permissionsgroups?.includes("Master"));

  const [textSearch, setTextSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [flagFilter, setFlagFilter] = useState("");
  const [paymentSourceFilter, setPaymentSourceFilter] = useState("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  // Bumped whenever the document panel (or a bulk action, an upload, a mail
  // check) changes something, so the month tree (periods summary + whichever
  // months are open), the tab counts and the missing-invoices banner all
  // catch up — each keeps its own state separate from this container's own.
  const [reloadToken, setReloadToken] = useState(0);
  // The tab badges. `null` until the first response lands — no badge is shown
  // meanwhile, so a "0" is never a guess. A company-wide count across every
  // tab, not the current tab's rows.
  const [counts, setCounts] = useState<PurchaseDocCounts | null>(null);
  const [checkingMail, setCheckingMail] = useState(false);
  // Who can move each tab's queue — fetched once per company, never on a tab
  // switch: the holders behind every permission key are the same list
  // regardless of which tab is selected, only which keys of it get shown
  // changes (see InvoiceInboxActors + TAB_ACTORS). `null` renders no strip,
  // both before the first response and if the fetch never succeeds.
  const [permissionHolders, setPermissionHolders] = useState<InvoicePermissionHolders | null>(null);
  // The peek at the file. Kept in component state rather than in the URL: it
  // is a glance, not a place — ?doc= stays the shareable "open this document".
  const [previewDoc, setPreviewDoc] = useState<PurchaseDocListItem | null>(null);
  // Bulk actions. The selection is of rows ON SCREEN: it is cleared when the
  // company, tab or filters change, and pruned to whatever the open months of
  // the tree still show after every reload — so a count in the bar is always
  // a count of rows the person can see.
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // What the month tree shows right now — every open month's current page.
  // The tree owns those rows; the selection lives here so the tree and the
  // bulk bar share one.
  const [treeDocs, setTreeDocs] = useState<PurchaseDocListItem[]>([]);
  const [bulkRunning, setBulkRunning] = useState<BulkActionId | null>(null);
  const [bulkResult, setBulkResult] = useState<BulkResult | null>(null);

  const tabParam = (searchParams.get("tab") || "needs_review") as PurchaseDocTab;
  const selectedTab = Math.max(0, TABS.findIndex((tab) => tab.id === tabParam));
  const openDocId = searchParams.get("doc");

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch((current) => (current === textSearch ? current : textSearch));
    }, 300);
    return () => window.clearTimeout(timeoutId);
  }, [textSearch]);

  const filters = useMemo<PurchaseDocFilters>(() => {
    const value: PurchaseDocFilters = {};
    if (debouncedSearch.trim()) value.textSearch = debouncedSearch.trim();
    if (flagFilter === "duplicates") value.duplicatesOnly = true;
    if (flagFilter === "bank") value.bankChangeOnly = true;
    if (flagFilter === "suspicious") value.suspiciousOnly = true;
    if (flagFilter === "not_entered") value.bookkept = "no";
    if (paymentSourceFilter) value.paymentSource = paymentSourceFilter as PurchaseDocFilters["paymentSource"];
    return value;
  }, [debouncedSearch, flagFilter, paymentSourceFilter]);

  // Tab badges. One count per tab, always through the same query builder the
  // tab's own rows use (see erp-backend docs/counts.js) — a badge and its
  // tab's list can never disagree. `reloadToken` is what makes this react to
  // a write anywhere in the register.
  const countsRequestRef = useRef(0);
  const loadCounts = useCallback(async () => {
    if (!companyId) return;
    const requestId = ++countsRequestRef.current;
    try {
      const data = await fetchPurchaseDocCounts(companyId, filters);
      if (requestId !== countsRequestRef.current) return;
      setCounts(data.counts || {});
    } catch (err) {
      // The old counts stay on screen — a stale badge beats none at all.
      if (requestId !== countsRequestRef.current) return;
      console.error("Failed to load the invoice inbox tab counts", err);
    }
  }, [companyId, filters]);

  // A company switch must never show the previous company's badges, even for
  // the instant before the new company's counts arrive — a stale "8" on the
  // wrong company's register is worse than no badge at all.
  useEffect(() => {
    setCounts(null);
  }, [companyId]);

  useEffect(() => {
    loadCounts();
  }, [loadCounts, reloadToken]);

  // Fetched once per company, not per tab: a company switch must not show
  // the previous company's holders while the new one is in flight, and a
  // failed (or, in tests, unmocked — resolving `undefined` rather than
  // rejecting) fetch must not throw — the register renders with no strip
  // rather than not at all. The backend 403s a user without register access,
  // but this screen is only reachable with it, so nothing here needs to gate
  // on that.
  const holdersRequestRef = useRef(0);
  useEffect(() => {
    if (!companyId) {
      setPermissionHolders(null);
      return;
    }
    const requestId = ++holdersRequestRef.current;
    setPermissionHolders(null);
    (async () => {
      try {
        const data = await fetchInvoicePermissionHolders(companyId);
        if (requestId !== holdersRequestRef.current) return;
        setPermissionHolders(data || null);
      } catch (err) {
        if (requestId !== holdersRequestRef.current) return;
        console.error("Failed to load the invoice inbox permission holders", err);
      }
    })();
  }, [companyId]);

  useEffect(() => {
    setSelectedIds([]);
    setBulkResult(null);
  }, [companyId, selectedTab, filters]);

  useEffect(() => {
    setSelectedIds((current) => {
      const onScreen = new Set(treeDocs.map((doc) => doc.id));
      const kept = current.filter((id) => onScreen.has(id));
      return kept.length === current.length ? current : kept;
    });
  }, [treeDocs]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const selectedDocs = useMemo(() => treeDocs.filter((doc) => selectedSet.has(doc.id)), [treeDocs, selectedSet]);

  // The selection is frozen while an action runs: emptying it would unmount
  // the bar — and its progress — while the requests carry on regardless.
  const toggleSelected = useCallback(
    (id: string) => {
      if (bulkRunning) return;
      setSelectedIds((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]));
    },
    [bulkRunning]
  );
  const setManySelected = useCallback(
    (ids: string[], selected: boolean) => {
      if (bulkRunning) return;
      setSelectedIds((current) => {
        const rest = current.filter((id) => !ids.includes(id));
        return selected ? [...rest, ...ids] : rest;
      });
    },
    [bulkRunning]
  );

  /**
   * Runs one bulk action over the selected rows it applies to.
   *
   * The selection is KEPT afterwards: "mark them reviewed, then send them for
   * approval" is two clicks on the same rows, not two rounds of ticking. Rows
   * that left the current tab because of the action drop out of it on the
   * reload, like any other row that is no longer on screen.
   */
  const runBulk = async (action: BulkAction, input: BulkInput) => {
    if (!companyId || bulkRunning) return;
    const targets = selectedDocs.filter(action.isEligible);
    if (!targets.length) return;
    setBulkRunning(action.id);
    setBulkResult(null);
    try {
      const result = await runBulkAction(action, companyId, targets, input);
      if (result.failed.length === 0) {
        toast.success(
          t("invoice_inbox_bulk_done", { defaultValue: "Done: {{count}} document(s).", count: result.succeeded.length })
        );
      } else {
        setBulkResult(result);
      }
    } finally {
      setBulkRunning(null);
      // Reloads the tree's open months and the tab counts.
      setReloadToken((n) => n + 1);
      // Approving takes documents off the person's approval list, even when the run stopped halfway: the bell follows.
      if (action.id === "approve") dispatch(fetchCabinetSummaryThunk(companyId));
    }
  };

  const describeBulkFailure = (failure: BulkFailure) => {
    if (failure.blockers.length) {
      return failure.blockers.map((b) => t(`invoice_inbox_blocker_${b}`, { defaultValue: b })).join(", ");
    }
    const key = failure.reason || failure.code;
    if (key) {
      return t(`invoice_inbox_bulk_error_${key}`, { defaultValue: failure.message || key });
    }
    return failure.message || t("invoice_inbox_action_failed", { defaultValue: "The action did not go through." });
  };
  const failedIds = new Set((bulkResult?.failed || []).map((failure) => failure.doc.id));

  const setTab = (index: number) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", TABS[index].id);
    next.delete("doc");
    // A manual tab click is a fresh browse, not a hop from a pill — the month
    // the previous tab had auto-expanded must not keep forcing itself open.
    next.delete("period");
    setSearchParams(next);
    setPreviewDoc(null);
  };

  /**
   * A pill under a grey/live month header: "8 of this month's docs are
   * awaiting approval" links straight into that tab, on that month — same
   * query-string approach as `rowUrl` below. `view` is no longer read by
   * anything, but it is still written so an OLDER tab of the app (or a link
   * copied before this change) keeps landing on the tree too.
   */
  const chipUrl = useCallback(
    (chipTab: PurchaseDocTab, periodKeyValue: string) => {
      const next = new URLSearchParams(searchParams);
      next.set("tab", chipTab);
      next.set("view", "tree");
      next.set("period", periodKeyValue);
      next.delete("doc");
      return `?${next.toString()}`;
    },
    [searchParams]
  );

  /**
   * "Check mail now" from the register itself.
   *
   * IDLE delivers within seconds and a sweep runs every five minutes, so this
   * is for the moment somebody is WAITING for a specific invoice and wants to
   * see it land rather than refresh the page for the next five minutes. It
   * sweeps every connected mailbox of the company, then reloads the tree —
   * the register, not just the mailbox row, is what the person is looking at.
   */
  const checkMail = async () => {
    if (!companyId) return;
    setCheckingMail(true);
    try {
      const res = await refreshInvoiceMailboxes(companyId);
      const results = res.mailboxes || [];
      const swept = results.filter((row) => row.status === "swept");
      const busyRows = results.filter((row) => row.status === "busy");
      const failed = results.filter((row) => row.status !== "swept" && row.status !== "busy");
      const processed = swept.reduce((sum, row) => sum + (row.processed || 0), 0);
      if (failed.length) {
        // offline / disconnected / timeout all mean the same to the person in
        // front of the screen: it was NOT checked. Name the mailbox, because a
        // company can have several and only one of them may be broken.
        toast.error(
          t("invoice_mailbox_not_checked", {
            defaultValue: "The mailbox was not checked ({{status}}).",
            status: failed.map((row) => (row.address || "") + " " + row.status).join(", ").trim(),
          })
        );
      } else if (processed) {
        toast.success(
          t("invoice_mailbox_swept_new", {
            defaultValue: "Checked — {{count}} new message(s).",
            count: processed,
          })
        );
      } else if (busyRows.length && !swept.length) {
        // A sweep that was already running inspected nothing on this click —
        // reporting "no new mail" would claim a check that did not happen.
        toast.success(t("invoice_mailbox_busy", { defaultValue: "A check is already running." }));
      } else {
        toast.success(t("invoice_mailbox_swept_none", { defaultValue: "Checked — no new mail." }));
      }
      setReloadToken((n) => n + 1);
    } catch (err: any) {
      const data = err && err.response && err.response.data;
      if (data && data.code === "NO_CONNECTED_MAILBOX") {
        toast.error(
          t("invoice_inbox_no_mailbox_connected", {
            defaultValue: "No mailbox is connected — connect one in the settings.",
          })
        );
      } else {
        toast.error((data && data.message) || err?.message || t("error"));
      }
    } finally {
      setCheckingMail(false);
    }
  };

  const closeDoc = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("doc");
    setSearchParams(next);
  };

  // The document panel can change status, review state, approvals — the tree
  // keeps its own state, so it is refreshed via the token rather than by
  // prop-drilling its rows into it. A bookkept toggle on the 'approved' or
  // 'bookkept' tab (the tree calls this as `onDocMoved`) moves the row to the
  // OTHER tab, which needs exactly the same refresh.
  const bumpReload = useCallback(() => setReloadToken((n) => n + 1), []);

  const rowUrl = (item: PurchaseDocListItem) => {
    const next = new URLSearchParams(searchParams);
    next.set("doc", item.id);
    return `?${next.toString()}`;
  };
  const hasFilters = Boolean(debouncedSearch.trim() || flagFilter || paymentSourceFilter);

  const secondaryActions: any[] = [];
  if (canManageSettings) {
    // Same right as the settings screen: the endpoints behind both reach into
    // the mailbox connection, so anyone else would only get a 403 from
    // buttons that looked available.
    secondaryActions.push({
      content: t("invoice_mailbox_check_now", { defaultValue: "Check mail now" }),
      onAction: checkMail,
      loading: checkingMail,
    });
    secondaryActions.push({
      content: t("settings", { defaultValue: "Settings" }),
      url: `/app/${companyId}/invoice-inbox/settings`,
    });
  }

  // Handed to the tree so it can show this instead of its own "nothing here"
  // placeholder when there are no periods at all under the tab+filters being
  // browsed — which, since even a month with documents only under another
  // status still appears as a grey period, only happens when the register (or
  // what the filters narrow it to) truly has nothing.
  const emptyState = (
    <Card>
      <EmptyState
        heading={
          hasFilters
            ? t("invoice_inbox_empty_filtered", { defaultValue: "Nothing matches these filters" })
            : t("invoice_inbox_empty", { defaultValue: "No documents here yet" })
        }
        image=""
      >
        <p>
          {hasFilters
            ? t("invoice_inbox_empty_filtered_hint", {
                defaultValue: "Clear the filters to see the rest of the register.",
              })
            : t("invoice_inbox_empty_hint", {
                defaultValue:
                  "Invoices sent to the company mailbox appear here within a few minutes. You can also upload one by hand.",
              })}
        </p>
        {/* "Nothing here" reads the same whether the mail is quiet or no
            mailbox was ever connected — so say where to connect one. */}
        {!hasFilters && canManageSettings && (
          <p className="mt-2">
            <Link to={`/app/${companyId}/invoice-inbox/settings`}>
              {t("invoice_inbox_empty_settings_link", { defaultValue: "Set up the mailbox invoices are sent to" })}
            </Link>
          </p>
        )}
      </EmptyState>
    </Card>
  );

  return (
    <Page
      fullWidth
      title={t("invoice_inbox", { defaultValue: "Incoming invoices" })}
      primaryAction={{
        content: t("invoice_inbox_upload", { defaultValue: "Upload a document" }),
        onAction: () => fileInputRef.current && fileInputRef.current.click(),
        loading: uploading,
      }}
      secondaryActions={secondaryActions}
    >
      {/* Uploading is a first-class action here, not a settings-screen
          afterthought: the pilot starts with a brand-new address, so for the
          first weeks most invoices arrive on paper or by forward. */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.tif,.tiff,.xml,.zip"
        onChange={async (event) => {
          const file = event.target.files && event.target.files[0];
          event.target.value = "";
          if (!file || !companyId) return;
          try {
            setUploading(true);
            setUploadError(null);
            await uploadPurchaseDoc(companyId, file);
            setReloadToken((n) => n + 1);
          } catch (err: any) {
            const data = err && err.response && err.response.data;
            setUploadError(
              (data && (data.message || data.code)) ||
                t("invoice_inbox_upload_failed", { defaultValue: "The file was not accepted." })
            );
          } finally {
            setUploading(false);
          }
        }}
      />

      {/* No `children` passed to Tabs: Polaris keys its own Panel by the
          selected tab id, so children handed to it are unmounted and
          remounted on every tab switch — losing the tree's expanded months
          and its spinner on every click. The register's content is rendered
          as Tabs' sibling instead, so a tab switch only ever changes which
          tab is highlighted. */}
      <Tabs
        tabs={TABS.map((tab) => ({
          id: tab.id,
          content: t(tab.labelKey, { defaultValue: tab.id }),
          panelID: `${tab.id}-panel`,
          // undefined until the first response — no badge is better than a
          // guessed one; "0" only appears once the server actually said so.
          badge: counts ? String(counts[tab.id] ?? 0) : undefined,
        }))}
        selected={selectedTab}
        onSelect={setTab}
      />
      <div className={`space-y-3 mt-3 ${PHONE_PAGE_GUTTER}`}>
        <InvoiceInboxActors
          tab={TABS[selectedTab].id}
          data={permissionHolders}
          companyId={companyId as string}
          isMaster={isMaster}
        />

        {uploadError && (
          <Banner tone="warning" onDismiss={() => setUploadError(null)}>
            {uploadError}
          </Banner>
        )}

        {bulkResult && bulkResult.failed.length > 0 && (
          <Banner tone="warning" onDismiss={() => setBulkResult(null)}>
            <p>
              {t("invoice_inbox_bulk_partial", {
                defaultValue: "Done: {{done}}. Did not go through: {{failed}} —",
                done: bulkResult.succeeded.length,
                failed: bulkResult.failed.length,
              })}
            </p>
            <ul className="list-disc ml-5">
              {bulkResult.failed.map((failure) => (
                <li key={failure.doc.id}>
                  #{failure.doc.docNumber ?? "—"}
                  {failure.doc.supplierNameRaw ? ` ${failure.doc.supplierNameRaw}` : ""}
                  {failure.doc.invoiceNumber ? ` (${failure.doc.invoiceNumber})` : ""}: {describeBulkFailure(failure)}
                </li>
              ))}
            </ul>
          </Banner>
        )}

        {companyId && <InvoiceInboxExpectations companyId={companyId} reloadToken={reloadToken} />}
        {companyId && <InvoiceInboxMailWithoutDocuments companyId={companyId} reloadToken={reloadToken} />}

        {/* One slim row: no Card, no stacked labels — everything above the
            first month should be about half of what it used to take. */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[220px] flex-1">
            <TextField
              label={t("search", { defaultValue: "Search" })}
              labelHidden
              value={textSearch}
              onChange={setTextSearch}
              placeholder={t("invoice_inbox_search_placeholder", {
                defaultValue: "Supplier, invoice number, file name…",
              })}
              autoComplete="off"
              clearButton
              onClearButtonClick={() => setTextSearch("")}
            />
          </div>
          <div className="w-full sm:w-auto sm:min-w-[240px]">
            <Select
              label={t("invoice_inbox_filter_flags", { defaultValue: "Only show" })}
              labelInline
              options={[
                { label: t("all", { defaultValue: "All" }), value: "" },
                {
                  label: t("invoice_inbox_flag_duplicate", { defaultValue: "Possible duplicates" }),
                  value: "duplicates",
                },
                {
                  label: t("invoice_inbox_flag_bank_change", { defaultValue: "Changed bank account" }),
                  value: "bank",
                },
                {
                  label: t("invoice_inbox_flag_suspicious", { defaultValue: "Suspicious content" }),
                  value: "suspicious",
                },
                {
                  label: t("invoice_inbox_flag_not_entered", { defaultValue: "Not entered into accounting yet" }),
                  value: "not_entered",
                },
              ]}
              value={flagFilter}
              onChange={setFlagFilter}
            />
          </div>
          <div className="w-full sm:w-auto sm:min-w-[240px]">
            <Select
              label={t("invoice_inbox_filter_payment_source", { defaultValue: "Paid by" })}
              labelInline
              options={[
                { label: t("all", { defaultValue: "All" }), value: "" },
                {
                  label: t("invoice_inbox_payment_source_employee", { defaultValue: "Paid by an employee" }),
                  value: "employee",
                },
                {
                  label: t("invoice_inbox_payment_source_company", { defaultValue: "Paid from the company account" }),
                  value: "company",
                },
                {
                  label: t("invoice_inbox_payment_source_unset", { defaultValue: "Not stated" }),
                  value: "unset",
                },
              ]}
              value={paymentSourceFilter}
              onChange={setPaymentSourceFilter}
            />
          </div>
        </div>

        <InvoiceInboxPeriodTree
          companyId={companyId as string}
          tab={TABS[selectedTab].id}
          filters={filters}
          reloadToken={reloadToken}
          rowUrl={rowUrl}
          onPreview={setPreviewDoc}
          selectedIds={selectedSet}
          selectionDisabled={Boolean(bulkRunning)}
          failedIds={failedIds}
          onToggleSelected={toggleSelected}
          onSetManySelected={setManySelected}
          onShownDocsChange={setTreeDocs}
          chipUrl={chipUrl}
          expandPeriodKey={searchParams.get("period")}
          onDocMoved={bumpReload}
          onCountsStale={loadCounts}
          pollPaused={uploading || checkingMail || Boolean(bulkRunning)}
          emptyState={emptyState}
        />
      </div>

      <InvoiceInboxBulkBar
        companyId={companyId as string}
        selectedDocs={selectedDocs}
        running={bulkRunning}
        onRun={runBulk}
        onClear={() => setSelectedIds([])}
      />

      {previewDoc && (
        <InvoiceDocPreview
          companyId={companyId as string}
          doc={previewDoc}
          docUrl={rowUrl(previewDoc)}
          onClose={() => setPreviewDoc(null)}
        />
      )}

      {openDocId && (
        // key={openDocId} remounts the panel per document, which is the whole
        // point: without it React reuses one instance and every draft field —
        // comment, rejection reason, the amounts being edited, the bank-verify
        // note — survives the switch. A rejection reason typed for one invoice
        // would then be submitted against the next one opened. The request-id
        // guard inside the panel stops a stale RESPONSE painting over; it
        // cannot clear what the user has already typed.
        <InvoiceDocPanel
          key={openDocId}
          companyId={companyId as string}
          docId={openDocId}
          onClose={closeDoc}
          onChanged={bumpReload}
        />
      )}
    </Page>
  );
}
